import { Component, Components, Panel, PanelItemGroup, ReferenceMap, Vertex3 } from '@rta/rta-blueprint-library';

import { panelItemGroupHasSleevePairFor } from './is-sleeve-pairing';

const ADJACENT_GROUP_GAP = 2;

// Spawns the new group clear of the group it came from, flipping left when the right would overflow the panel.
const getAdjacentX = (sourceGroup: PanelItemGroup, panel: Panel): number => {
	const offset = (sourceGroup.width / 2) + ADJACENT_GROUP_GAP;
	const rightX = sourceGroup.center.x + offset;

	return rightX <= panel.size.width / 2 ? rightX : sourceGroup.center.x - offset;
};

export type ResolvePanelItemGroupParams = {
	refMap: ReferenceMap;
	placement: string;
	fallbackX: number;
	incoming?: Component | null;
};

/**
 * Resolves which PanelItemGroup a newly added item belongs to.
 * A PanelItemGroup is a vertical stack sharing one x, so top-only items -- which sit on the
 * countertop rather than in the stack -- get their own group when dropped on open panel space;
 * sharing one there would pin their x to whatever appliance happens to be below and block
 * independent dragging. But refMap.panelItemGroup is only ever populated when the user targeted
 * that specific group -- via its own "Add Appliance" menu (the group's full-height overlay) or
 * via a specific item inside it (a swap) -- so at that point the column has already been chosen
 * for them, and a TOP_ONLY item should join it instead of spawning an independent one elsewhere
 * on the panel. (This is what let a sink "attach" to the access door it was dropped on: the door's
 * group.) The other join case is a sleeve pairing: the C1CHCS asado is TOP_ONLY (Smokers category
 * default) but nests inside a sibling sleeve, so if the current group already has that sleeve
 * wrapper it must keep the incoming asado in the same column even with no group explicitly
 * targeted -- otherwise `getPanelSleeveCfn` can't resolve the pairing on read.
 * @param params refMap for the add, the new item's placement, the x to use when there is no source group, and (optional) the incoming Component so a TOP_ONLY sleeve occupant can join its wrapper's group and its catalog defaultPlacement can seed the new group's x
 * @returns the group to add the item to, or null when the refMap has no panel
 */
export const resolvePanelItemGroup = (params: ResolvePanelItemGroupParams): PanelItemGroup | null => {
	const { refMap, placement, fallbackX, incoming = null } = params;
	const { panel = null, panelItemGroup = null } = refMap ?? {};

	if (!panel) return null;

	// the user targeted this group directly -- on itself or on one of its items -- so it keeps that group
	const targetsExistingGroup = Boolean(panelItemGroup);
	const joinsSleevePair = panelItemGroupHasSleevePairFor(panelItemGroup, incoming);
	const needsOwnGroup = placement === Components.PLACEMENT_TOP_ONLY && !targetsExistingGroup && !joinsSleevePair;

	if (panelItemGroup && !needsOwnGroup) return panelItemGroup;

	const group = panel.panelItemGroups.new();
	const defaultCenterX = group.getDefaultPlacementCenterX(incoming);
	group.center = new Vertex3({ dataIn: { x: defaultCenterX ?? (panelItemGroup ? getAdjacentX(panelItemGroup, panel) : fallbackX) }});

	return group;
};
